<template>
  <div class="viewer-container">
    <div ref="speckleContainer" class="speckle-container"></div>
    
    <!-- Labels Overlay -->
    <div 
      class="labels-overlay" 
      v-if="filteredObjectLabels.length > 0"
      @touchstart="handleLabelTouch"
      @touchmove.prevent.stop="handleLabelTouch"
      @touchend="handleLabelTouch"
      @touchcancel="handleLabelTouch"
    >
      <div 
        v-for="label in filteredObjectLabels" 
        :key="label.id"
        v-show="label.visible && label.properties.temperature !== undefined"
        class="object-label"
        :style="getLabelStyle(label)"
        @click="openTemperatureHistory(label)"
        @wheel.prevent.stop="handleLabelWheel"
      >
        <h3 class="label-text"><span v-if="label.properties.temperature !== undefined">{{ label.properties.temperature }} °C</span><span v-else>- C°</span></h3>
        <span class="label-text">{{ label.properties.identifiant }}-sensor</span>
      </div>
    </div>
    
    <!-- Left Sidebar Overlay -->
    <div class="sidebar-overlay">
      <div class="sidebar-tools">
        <!-- Filter Toggle -->
        <button class="filter-toggle-btn" @click="showFilters = !showFilters; if (showFilters) { showTransform = false; showPicker = false; }" title="Filtres" aria-label="Filtres">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
            <polyline points="2 12 12 17 22 12"></polyline>
            <polyline points="2 17 12 22 22 17"></polyline>
          </svg>
          <span v-if="activeFilter" class="active-filter-indicator"></span>
        </button>

        <!-- Transform Toggle -->
        <!-- <button class="filter-toggle-btn" @click="showTransform = !showTransform; if (showTransform) { showFilters = false; showPicker = false; }" title="Transformer" aria-label="Transformer">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="5 9 2 12 5 15"></polyline><polyline points="9 5 12 2 15 5"></polyline><polyline points="19 9 22 12 19 15"></polyline><polyline points="9 19 12 22 15 19"></polyline><line x1="2" y1="12" x2="22" y2="12"></line><line x1="12" y1="2" x2="12" y2="22"></line>
          </svg>
          <span v-if="showTransform" class="active-filter-indicator"></span>
        </button> -->

        <!-- Picking Toggle -->
        <button class="filter-toggle-btn" @click="togglePicker" title="Capturer coordonnées" aria-label="Picker">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="22" y1="12" x2="18" y2="12"></line>
            <line x1="6" y1="12" x2="2" y2="12"></line>
            <line x1="12" y1="6" x2="12" y2="2"></line>
            <line x1="12" y1="22" x2="12" y2="18"></line>
          </svg>
          <span v-if="isPicking" class="active-filter-indicator indicator-picking"></span>
        </button>
      </div>

      <div class="sidebar-panels">
        <Transition name="fade-slide">
          <div v-show="showFilters" class="filter-dropdown">
            <div v-if="availableLevels.length > 0" class="filter-group">
              <h4 class="filter-title">Niveaux</h4>
              <button 
                v-for="level in availableLevels" 
                :key="level"
                class="filter-button" 
                :class="{ 'is-active': activeFilter === level }"
                @click="setFilter(level)"
                :title="`Filtrer ${level === 'Aucun' ? 'sans niveau' : level}`" 
                :aria-label="`Filtrer ${level === 'Aucun' ? 'sans niveau' : level}`">
                {{ level }}
              </button>
            </div>
            <div v-else class="filter-group-loading">
              <span v-if="loading" class="pulse-small"></span>
              <span v-else>Aucun filtre</span>
            </div>
            <div style="height: 1px; background: rgba(255, 255, 255, 0.1); margin: 8px 0;"></div>
              <button 
                class="filter-button" 
                :class="{ 'is-active': showPointCloud }"
                @click="togglePointCloud"
                title="Toggle Pointcloud" 
                aria-label="Toggle Pointcloud">
                <svg v-if="showPointCloud" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
                Pointcloud
              </button>
              
            <div style="height: 1px; background: rgba(255, 255, 255, 0.1); margin: 8px 0;"></div>
            
            <div class="filter-group">
              <h4 class="filter-title">Filtre des Labels</h4>
              <button 
                class="filter-button" 
                :class="{ 'is-active': activeIoT }"
                @click="activeIoT = !activeIoT"
                title="IoT" 
                aria-label="IoT">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
                IoT
              </button>
            </div>
            
          </div>
        </Transition>

        <Transition name="fade-slide">
          <div v-show="showTransform" class="filter-dropdown transform-panel">
            <div class="filter-group block-mt">
              <h4 class="filter-title">Position</h4>
              <div class="input-group">
                <label>X</label> <input type="number" step="1" v-model="transformState.posX" @input="applyTransform" />
              </div>
              <div class="input-group">
                <label>Y</label> <input type="number" step="1" v-model="transformState.posY" @input="applyTransform" />
              </div>
              <div class="input-group">
                <label>Z</label> <input type="number" step="1" v-model="transformState.posZ" @input="applyTransform" />
              </div>
            </div>
            
            <div class="filter-group block-mt">
              <h4 class="filter-title">Rotation (°)</h4>
              <div class="input-group">
                <label>X</label> <input type="number" step="1" v-model="transformState.rotX" @input="applyTransform" />
              </div>
              <div class="input-group">
                <label>Y</label> <input type="number" step="1" v-model="transformState.rotY" @input="applyTransform" />
              </div>
              <div class="input-group">
                <label>Z</label> <input type="number" step="1" v-model="transformState.rotZ" @input="applyTransform" />
              </div>
            </div>
          </div>
        </Transition>
        <Transition name="fade-slide">
          <div v-show="showPicker" class="filter-dropdown picker-panel">
            <div class="filter-group block-mt">
              <h4 class="filter-title">Capture de points</h4>
              <!-- <p class="picker-help">Cliquez sur un point du nuage pour obtenir ses coordonnées.</p> -->
              
              <button 
                class="filter-button" 
                :class="{ 'is-active': isPicking }"
                @click="isPicking = !isPicking"
              >
                {{ isPicking ? 'Mode Capture : ACTIF' : 'Mode Capture : PAUSE' }}
              </button>

              <div v-if="pickedCoords" class="picked-results block-mt">
                <div class="coord-row">
                  <span class="coord-label">X:</span>
                  <span class="coord-val">{{ pickedCoords.x.toFixed(3) }}</span>
                </div>
                <div class="coord-row">
                  <span class="coord-label">Y:</span>
                  <span class="coord-val">{{ pickedCoords.y.toFixed(3) }}</span>
                </div>
                <div class="coord-row">
                  <span class="coord-label">Z:</span>
                  <span class="coord-val">{{ pickedCoords.z.toFixed(3) }}</span>
                </div>
                
                <!-- <button class="copy-btn block-mt" @click="copyCoords">
                  Copier les coordonnées
                </button> -->
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Right Sidebar Overlay for Object Info -->
    <div class="info-sidebar" v-if="selectedObject">
      <h3>Propriétés de l'objet</h3>
      <button class="close-btn" @click="closeInfo" title="Fermer" aria-label="Fermer">✕</button>
      <div class="properties-list">
        <div v-for="(value, key) in filterProperties(selectedObject)" :key="key" class="property-item">
          <span class="property-key">{{ key }}</span>
          <span class="property-value">{{ value }}</span>
        </div>
      </div>
    </div>

    <Transition name="fade">
      <div class="overlay" v-if="showOverlay">
        <div class="stats" v-if="loading">
          <span class="pulse"></span> Loading model data...
        </div>
        <div class="stats" v-else>
          <span class="success"></span> Model loaded successfully
        </div>
      </div>
    </Transition>

    <div v-if="debugInfo" class="debug-overlay">
      <pre>{{ debugInfo }}</pre>
    </div>

    <!-- Temperature History Modal Overlay -->
    <Transition name="fade-scale">
      <div v-if="activeTempHistorySensor" class="modal-backdrop" @click.self="closeTempHistory">
        <div class="modal-content glassmorphism-modal">
          <header class="modal-header">
            <div class="modal-title">
                <h3>{{ activeTempHistorySensor.properties.identifiant }}-sensor</h3>
            </div>
            <button class="modal-close-btn" @click="closeTempHistory" title="Fermer" aria-label="Fermer">✕</button>
          </header>
          
          <div class="modal-body">

            <!-- Error message if validation fails -->
            <div v-if="validationError" class="validation-error-msg">
              {{ validationError }}
            </div>

            <!-- Loading state -->
            <div v-if="historyLoading" class="modal-loading">
              <span class="pulse-large"></span>
              <p>Fetching historical data...</p>
            </div>
            
            <!-- Error state -->
            <div v-else-if="historyError" class="modal-error">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <p>{{ historyError }}</p>
              <button class="retry-btn" @click="fetchTempHistory">Retry</button>
            </div>
            
            <!-- Graph Content -->
            <div v-else class="graph-container">
              <!-- Stats panel -->
              <div class="stats-row">
                <div class="stat-card">
                  <span class="stat-label">Current</span>
                  <span class="stat-value current">{{ activeTempHistorySensor.properties.temperature }} °C</span>
                </div>
                <div class="stat-card">
                  <span class="stat-label">Minimum</span>
                  <span class="stat-value min">{{ historyStats.min.toFixed(1) }} °C</span>
                </div>
                <div class="stat-card">
                  <span class="stat-label">Maximum</span>
                  <span class="stat-value max">{{ historyStats.max.toFixed(1) }} °C</span>
                </div>
                <div class="stat-card">
                  <span class="stat-label">Average</span>
                  <span class="stat-value avg">{{ historyStats.avg.toFixed(1) }} °C</span>
                </div>
              </div>
              
              <!-- SVG Graph -->
              <div class="svg-graph-wrapper">
                <div class="svg-graph-container">
                  <svg 
                    width="100%" 
                    height="260" 
                    viewBox="0 0 600 260" 
                    preserveAspectRatio="none"
                    @mousemove="handleGraphMouseMove"
                    @mouseleave="handleGraphMouseLeave"
                    class="svg-graph"
                  >
                    <!-- Definitions for gradients -->
                    <defs>
                      <linearGradient id="area-grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stop-color="var(--accent-color)" stop-opacity="0.35" />
                        <stop offset="100%" stop-color="var(--accent-color)" stop-opacity="0" />
                      </linearGradient>
                      <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stop-color="#00c8cb" />
                        <stop offset="50%" stop-color="#3b82f6" />
                        <stop offset="100%" stop-color="#8b5cf6" />
                      </linearGradient>
                    </defs>
                    
                    <!-- Grid Lines -->
                    <g class="grid-lines">
                      <line 
                        v-for="gridY in gridYLines" 
                        :key="gridY.val"
                        x1="50" 
                        :y1="gridY.y" 
                        x2="550" 
                        :y2="gridY.y" 
                        stroke="rgba(255, 255, 255, 0.05)" 
                        stroke-width="1"
                      />
                    </g>
                    
                    <!-- Y Axis Labels -->
                    <g class="axis-labels y-labels">
                      <text 
                        v-for="gridY in gridYLines" 
                        :key="'lbl-'+gridY.val"
                        x="40" 
                        :y="gridY.y + 4" 
                        text-anchor="end" 
                        fill="var(--text-secondary)"
                        font-size="14"
                      >
                        {{ gridY.val.toFixed(1) }}°
                      </text>
                    </g>
                    
                    <!-- Graph Path and Area -->
                    <path :d="graphPaths.area" fill="url(#area-grad)" />
                    <path :d="graphPaths.line" fill="none" stroke="url(#line-grad)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
                    
                    <!-- X Axis Labels -->
                    <g class="axis-labels x-labels">
                      <text 
                        v-for="tick in gridXLabels" 
                        :key="tick.time"
                        :x="tick.x" 
                        y="245" 
                        text-anchor="middle" 
                        fill="var(--text-secondary)"
                        font-size="14"
                      >
                        {{ tick.label }}
                      </text>
                    </g>
                    
                    <!-- Tooltip vertical line indicator -->
                    <line 
                      v-if="hoveredPoint" 
                      :x1="hoveredPoint.x" 
                      y1="25" 
                      :x2="hoveredPoint.x" 
                      y2="215" 
                      stroke="rgba(255, 255, 255, 0.3)" 
                      stroke-width="1" 
                      stroke-dasharray="4 4" 
                    />
                    
                  </svg>
                  
                  <!-- Tooltip HTML popup positioned over SVG -->
                  <div 
                    v-if="hoveredPoint" 
                    class="graph-tooltip"
                    :style="{ 
                      left: `clamp(10px, calc(${((hoveredPoint.x / 600) * 100)}% - 55px), calc(100% - 120px))`, 
                      top: `${hoveredPoint.y - 70}px` 
                    }"
                  >
                    <div class="tooltip-time">{{ hoveredPoint.formattedTime }}</div>
                    <div class="tooltip-val">{{ hoveredPoint.value.toFixed(1) }} °C</div>
                  </div>

                  <!-- Tooltip dot on the line (HTML to avoid SVG preserveAspectRatio="none" scaling/flattening) -->
                  <div 
                    v-if="hoveredPoint" 
                    class="graph-dot"
                    :style="{ 
                      left: `${((hoveredPoint.x / 600) * 100)}%`, 
                      top: `${hoveredPoint.y}px` 
                    }"
                  ></div>
                </div>
              </div>
            </div>

            <!-- Date controls row -->
            <div class="modal-controls">
              <div class="date-input-group">
                <label>Du</label>
                <input 
                  type="date" 
                  v-model="startDate" 
                  @change="validateAndFetch" 
                  class="datetime-input"
                />
              </div>
              <div class="date-input-group">
                <label>Au</label>
                <input 
                  type="date" 
                  v-model="endDate" 
                  @change="validateAndFetch" 
                  class="datetime-input"
                />
              </div>
              <button class="refresh-btn" @click="validateAndFetch" title="Actualiser" aria-label="Actualiser">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
                </svg>
              </button>
            </div>


          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, computed, watch } from 'vue'
import * as THREE from 'three'
import { Viewer, DefaultViewerParams, SpeckleLoader, UrlHelper, CameraController, SelectionExtension, FilteringExtension, ViewerEvent, SpeckleType, CameraEvent } from "@speckle/viewer"
import { CopcExtension } from "~/utils/CopcExtension"

// Container reference to mount the viewer
const speckleContainer = ref<HTMLElement | null>(null)
const loading = ref(true)
const showOverlay = ref(true)
const showFilters = ref(false)
const showTransform = ref(false)
const showPicker = ref(false)
const isPicking = ref(false)
const pickedCoords = ref<{ x: number, y: number, z: number } | null>(null)
const activeFilter = ref<string | null>(null)
const availableLevels = ref<string[]>([])
const selectedObject = ref<any>(null)
const debugInfo = ref<string>('') // NEW DEBUG INFO
const transformState = ref({ posX: -11, posY: -6, posZ: 4, rotX: 0, rotY: 0, rotZ: 36.8 })
const showPointCloud = ref(true)
let viewer: Viewer | null = null
let resizeObserver: ResizeObserver | null = null
let hideOverlayTimeout: ReturnType<typeof setTimeout> | null = null
const config = useRuntimeConfig()

interface ObjectLabel {
  id: string
  name: string
  category?: string
  position3D: THREE.Vector3
  x: number
  y: number
  visible: boolean
  properties: {
    name: string
    level?: string
    worksetName?: string
    identifiant?: string
    temperature?: number
  }
}

const objectLabels = ref<ObjectLabel[]>([])
const activeIoT = ref(true)

// Filter labels dynamically based on category and active level filter
const filteredObjectLabels = computed(() => {
  let labels = objectLabels.value

  if (activeIoT.value) {
    labels = labels.filter(label => label.properties.worksetName === 'IoT')
  } else {
    labels = labels.filter(label => label.properties.worksetName === '$-$')
  }

  if (activeFilter.value) {
    if (activeFilter.value === 'Aucun') {
      labels = labels.filter(label => !label.properties.level || label.properties.level === 'null' || label.properties.level === '')
    } else {
      labels = labels.filter(label => label.properties.level === activeFilter.value)
    }
  }

  return labels
})

// Hide non-matching labels and update positions when filter changes
watch(activeIoT, () => {
  for (const label of objectLabels.value) {
    label.visible = true
  }
  updateLabels()
})

const togglePicker = () => {
  showPicker.value = !showPicker.value
  if (showPicker.value) {
    showFilters.value = false
    showTransform.value = false
    isPicking.value = true
    console.log("Picking mode AUTO-ACTIVATED")
  } else {
    isPicking.value = false
    console.log("Picking mode DE-ACTIVATED")
  }
}

const copyCoords = () => {
  if (!pickedCoords.value) return
  const text = `${pickedCoords.value.x.toFixed(3)}, ${pickedCoords.value.y.toFixed(3)}, ${pickedCoords.value.z.toFixed(3)}`
  navigator.clipboard.writeText(text)
  alert('Coordonnées copiées !')
}

const handleViewerClick = (event: MouseEvent | PointerEvent) => {
  console.log("DEBUG: handleViewerClick entry", { 
    isPicking: isPicking.value, 
    viewer: !!viewer, 
    container: !!speckleContainer.value 
  })
  
  if (!isPicking.value || !viewer || !speckleContainer.value) return
  
  // Prevent Speckle or other extensions from reacting to this click
  event.stopPropagation()
  // event.preventDefault() // Optional, might block other things
  
  const rect = speckleContainer.value.getBoundingClientRect()
  const mouse = new THREE.Vector2()
  mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
  mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
  
  const raycaster = new THREE.Raycaster()
  // Try to enable layers 0 and 1 just in case
  raycaster.layers.enable(0)
  raycaster.layers.enable(1)
  
  // Set a generous threshold
  raycaster.params.Points.threshold = 2.0 
  
  const renderer = (viewer as any).getRenderer()
  const camera = renderer.renderingCamera || renderer.camera
  if (!camera) {
    console.error("No camera found for raycasting")
    return
  }

  raycaster.setFromCamera(mouse, camera)
  
  const copcExt = viewer.getExtension(CopcExtension)
  if (!copcExt || !copcExt.pointCloudGroup) {
    console.error("CopcExtension not found or group missing")
    return
  }
  
  // VITAL: Force world matrix update before raycasting
  copcExt.pointCloudGroup.updateMatrixWorld(true)
  
  // Raycast against the group and all its children
  const intersects = raycaster.intersectObject(copcExt.pointCloudGroup, true)
  
  console.log(`Raycasting: ${intersects.length} hits found`)
  
  if (intersects.length > 0 && intersects[0]) {
    const point = intersects[0].point
    pickedCoords.value = { x: point.x, y: point.y, z: point.z }
    console.log("SUCCESS: Point captured:", pickedCoords.value)
    
    addTemporaryCube(viewer, point);
    // Play a tiny feedback ?
    // debugInfo.value = `CAPTURED: X:${point.x.toFixed(3)} Y:${point.y.toFixed(3)} Z:${point.z.toFixed(3)}`
  } else {
    // If no point found, clear the current coordinates as requested
    pickedCoords.value = null
    console.log("No point cloud intersection found (coordinates cleared)")
    
    // List some children info for debugging
    const childCount = copcExt.pointCloudGroup.children.length
    if (childCount > 0) {
        const firstChild = copcExt.pointCloudGroup.children[0]
        if (firstChild) {
            console.log(`First child layers mask: ${firstChild.layers.mask}`)
        }
    }
  }
}

const addTemporaryCube = (viewer: Viewer, point: { x: number, y: number, z: number }): void => {
    try {
        const renderer = (viewer as any).getRenderer();
        const threeScene: THREE.Scene = renderer.scene;
        
        // 2. Créer la géométrie et le matériau Three.js
        // Augmenter la taille pour être sûr de le voir (50x50x50 car le nuage est à l'échelle 100x)
        const geometry: THREE.BoxGeometry = new THREE.BoxGeometry(0.1, 0.1, 0.1);
        const material: THREE.MeshBasicMaterial = new THREE.MeshBasicMaterial({ 
            color: 0xff0000,
            transparent: true,
            opacity: 0.8,
            depthTest: true
        });
        const cube: THREE.Mesh = new THREE.Mesh(geometry, material);

        // 3. Positionner l'objet dans le monde 3D
        cube.position.set(point.x, point.y, point.z);

        // VITAL: Régler le layer à 1 (STREAM_CONTENT) pour que Speckle l'affiche
        cube.layers.set(1);

        // 4. Ajouter l'objet à la scène
        threeScene.add(cube);

        // Forcer le rendu
        if (typeof (viewer as any).requestRender === 'function') {
            (viewer as any).requestRender();
        }

        console.log("Cube temporaire ajouté à la scène à :", cube.position);
    } catch (e) {
        console.error("Erreur lors de l'ajout du cube :", e);
    }
}

const togglePointCloud = () => {
  showPointCloud.value = !showPointCloud.value
  if (!viewer) return
  const extension = viewer.getExtension(CopcExtension)
  if (extension && extension.pointCloudGroup) {
    extension.pointCloudGroup.visible = showPointCloud.value
    if (typeof viewer.requestRender === 'function') {
      viewer.requestRender()
    }
  }
}

const applyTransform = () => {
  if (!viewer) return
  const extension = viewer.getExtension(CopcExtension)
  if (extension && extension.pointCloudGroup) {
    extension.pointCloudGroup.position.set(
      Number(transformState.value.posX) || 0,
      Number(transformState.value.posY) || 0,
      Number(transformState.value.posZ) || 0
    )
    extension.pointCloudGroup.rotation.set(
      THREE.MathUtils.degToRad(Number(transformState.value.rotX) || 0),
      THREE.MathUtils.degToRad(Number(transformState.value.rotY) || 0),
      THREE.MathUtils.degToRad(Number(transformState.value.rotZ) || 0)
    )
    extension.pointCloudGroup.updateMatrixWorld(true)
    if (typeof viewer.requestRender === 'function') {
      viewer.requestRender()
    }
  }
}

// Safe property extractor that resolves potential Speckle parameter wrappers
const extractPropertyValue = (val: any): any => {
  if (val && typeof val === 'object' && 'value' in val) {
    return val.value
  }
  return val
}

// Helper to filter out complex speckle technical properties and recursively flatten object data
const filterProperties = (obj: any, prefix = ''): Record<string, string> => {
  if (!obj) return {}
  let result: Record<string, string> = {}
  
  for (const [key, value] of Object.entries(obj)) {
    // Skip internal speckle properties or completely complex geometric objects
    if (key.startsWith('speckle_') || key.startsWith('__') || key === 'renderMaterial' || key === 'displayValue' || key === 'bbox' || key === 'baseLine' || key === 'baseCurve') continue
    
    // Construct the display key (e.g. "parameters.Niveau.value")
    const fullKey = prefix ? `${prefix}.${key}` : key

    if (value === null || value === undefined) {
      result[fullKey] = '-'
    } else if (Array.isArray(value)) {
      if (value.length === 0) {
        result[fullKey] = '[]'
      } else if (typeof value[0] !== 'object') {
        result[fullKey] = `[${value.join(', ')}]`
      } else {
        result[fullKey] = `[ ${value.length} items ]`
        // Optionally map a few child elements if needed
      }
    } else if (typeof value === 'object') {
      // Cast value to record to silence TS errors
      const objValue = value as Record<string, any>
      // If it's a typical Speckle parameter wrapper { value: ..., units: ... }
      if (objValue.value !== undefined) {
        result[fullKey] = String(objValue.value) + (objValue.units ? ` ${objValue.units}` : '')
      } else if (objValue.name && Object.keys(objValue).length <= 3) {
        result[fullKey] = String(objValue.name)
      } else {
        // Recursively extract nested objects (like 'parameters' or 'properties')
        result = { ...result, ...filterProperties(value, fullKey) }
      }
    } else {
      // Primitive values (string, number, boolean)
      result[fullKey] = String(value)
    }
  }
  return result
}

const closeInfo = () => {
  selectedObject.value = null
  if (viewer) {
    const selection = viewer.getExtension(SelectionExtension)
    if (selection) selection.clearSelection()
  }
}

// Apply a specific level filter to the viewer
const setFilter = (level: string) => {
  if (!viewer) return
  
  const filtering = viewer.getExtension(FilteringExtension)
  if (!filtering) return

  if (activeFilter.value === level) {
    // Reset all filters if clicking the already active one
    filtering.resetFilters()
    activeFilter.value = null
  } else {
    // Set new active filter
    activeFilter.value = level
    let filteredObjects = viewer.getWorldTree()
      .findAll((node: any) => {
        const levelName = node.model?.raw?.level || node.model.level?.name || node.model.level || node.model.parameters?.Niveau || ''
        
        if (level === 'Aucun') {
          // No level case
          return !levelName || levelName === 'null' || levelName === ''
        }
        
        // Specific level case
        return levelName === level
      })
      .map((node: any) => node.model.id)

    if (filteredObjects.length > 0) {
      // Using isolateObjects prevents hiding parents from hiding the children we want to show
      // filtering.isolateObjects(ids, stateKey, includeDescendants, ghost (true = transparent, false = hidden))
      filtering.resetFilters()
      filtering.isolateObjects(filteredObjects, 'filter', true, false)
    } else {
      // If no objects found, reset filters to show all
      filtering.resetFilters()
    }
  }
  
  // Update labels to position/filter the ones that match the level filter
  updateLabels()
}

// Project 3D positions of named objects to 2D HTML space
const updateLabels = () => {
  if (!viewer || !speckleContainer.value || filteredObjectLabels.value.length === 0) return

  const cameraController = viewer.getExtension(CameraController)
  const camera = cameraController?.renderingCamera
  if (!camera) return

  const rect = speckleContainer.value.getBoundingClientRect()
  const width = rect.width
  const height = rect.height

  const tempV = new THREE.Vector3()
  const camDir = new THREE.Vector3()
  camera.getWorldDirection(camDir)

  for (const label of filteredObjectLabels.value) {
    tempV.copy(label.position3D)
    
    // Project 3D point to NDC
    tempV.project(camera)
    
    // Check if the point is behind the camera plane
    const toPoint = new THREE.Vector3().subVectors(label.position3D, camera.position)
    const isBehind = toPoint.dot(camDir) < 0

    // Also check if it's way outside the viewport boundaries in NDC space
    // NDC coordinates go from -1 to 1. Allowing a tiny buffer of 0.05
    if (isBehind || Math.abs(tempV.x) > 1.05 || Math.abs(tempV.y) > 1.05) {
      label.visible = false
    } else {
      label.visible = true
      label.x = (tempV.x * 0.5 + 0.5) * width
      label.y = (-(tempV.y * 0.5) + 0.5) * height
    }
  }
}

/**
 * Forward wheel/scroll events from the labels overlay to the 3D viewer container.
 * This allows camera zoom/scroll to work even when the cursor is hovering over a label.
 */
const handleLabelWheel = (event: WheelEvent): void => {
  event.preventDefault()
  event.stopPropagation()

  if (!speckleContainer.value) return

  // Find the canvas element inside the Speckle container to direct the event correctly
  const target = speckleContainer.value.querySelector('canvas') || speckleContainer.value

  // Create a new WheelEvent with the same parameters cloned from the original event
  const forwardedEvent = new WheelEvent('wheel', {
    deltaX: event.deltaX,
    deltaY: event.deltaY,
    deltaZ: event.deltaZ,
    deltaMode: event.deltaMode,
    clientX: event.clientX,
    clientY: event.clientY,
    screenX: event.screenX,
    screenY: event.screenY,
    bubbles: true,
    cancelable: true,
    composed: true,
    shiftKey: event.shiftKey,
    ctrlKey: event.ctrlKey,
    altKey: event.altKey,
    metaKey: event.metaKey
  })

  // Dispatch the cloned event to the target canvas/container
  target.dispatchEvent(forwardedEvent)
}

/**
 * Forward touch events from the labels to the 3D viewer container to support
 * gestures like pinch-to-zoom and pan on touch devices (e.g., iPad).
 */
const handleLabelTouch = (event: TouchEvent): void => {
  if (!speckleContainer.value) return
  const target = speckleContainer.value.querySelector('canvas') || speckleContainer.value

  // Clone the TouchList items to target the canvas
  const cloneTouches = (touches: TouchList): Touch[] => {
    return Array.from(touches).map(t => {
      return new Touch({
        identifier: t.identifier,
        target: target,
        clientX: t.clientX,
        clientY: t.clientY,
        screenX: t.screenX,
        screenY: t.screenY,
        pageX: t.pageX,
        pageY: t.pageY
      })
    })
  }

  // Create a new TouchEvent with cloned touches targeting the canvas
  const forwardedEvent = new TouchEvent(event.type, {
    touches: cloneTouches(event.touches),
    targetTouches: cloneTouches(event.targetTouches),
    changedTouches: cloneTouches(event.changedTouches),
    bubbles: true,
    cancelable: true,
    composed: true,
    shiftKey: event.shiftKey,
    ctrlKey: event.ctrlKey,
    altKey: event.altKey,
    metaKey: event.metaKey
  })

  // Dispatch the cloned event to the canvas/container
  target.dispatchEvent(forwardedEvent)
}

// Active sensor for temperature history modal
const activeTempHistorySensor = ref<ObjectLabel | null>(null)
const historyLoading = ref(false)
const historyError = ref<string | null>(null)

// Date selection for temperature history
const startDate = ref<string>('')
const endDate = ref<string>('')
const validationError = ref<string | null>(null)

/**
 * Format Date to local date string format required by input type="date" (YYYY-MM-DD)
 */
const formatDateOnly = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Validate selected date range and fetch data if valid
 */
const validateAndFetch = (): void => {
  validationError.value = null
  if (!startDate.value || !endDate.value) {
    validationError.value = "Please select both a start date and an end date."
    return
  }
  
  const startParts = startDate.value.split('-').map(Number)
  const endParts = endDate.value.split('-').map(Number)
  
  const startMs = new Date(startParts[0]!, startParts[1]! - 1, startParts[2]!, 0, 0, 0).getTime()
  const endMs = new Date(endParts[0]!, endParts[1]! - 1, endParts[2]!, 23, 59, 59).getTime()
  
  if (startMs > endMs) {
    validationError.value = "The start date must be earlier than or equal to the end date."
    return
  }
  
  fetchTempHistory()
}

interface HistoryPoint {
  time: Date
  value: number
}

const historyData = ref<HistoryPoint[]>([])
const hoveredPointIndex = ref<number | null>(null)

// Graph Layout Dimensions
const graphWidth = 600
const graphHeight = 260
const paddingLeft = 50
const paddingRight = 50
const paddingTop = 25
const paddingBottom = 45

// Fetch the temperature history for the active sensor from Prometheus
const fetchTempHistory = async () => {
  if (!activeTempHistorySensor.value) return
  
  historyLoading.value = true
  historyError.value = null
  historyData.value = []
  
  try {
    const identifiant = activeTempHistorySensor.value.properties.identifiant
    if (!identifiant) {
      throw new Error("Sensor identifier is missing.")
    }
    
    const apiUrl = config.public.mintcoreApiUrl || 'http://localhost:3070'
    const token = config.public.mintcoreToken
    
    if (!token) {
      throw new Error("Authentication token is missing from runtimeConfig.")
    }
    
    if (!startDate.value || !endDate.value) {
      throw new Error("Start date or end date is missing.")
    }
    
    const startParts = startDate.value.split('-').map(Number)
    const endParts = endDate.value.split('-').map(Number)
    
    const startLocal = new Date(startParts[0]!, startParts[1]! - 1, startParts[2]!, 0, 0, 0)
    const endLocal = new Date(endParts[0]!, endParts[1]! - 1, endParts[2]!, 23, 59, 59)
    
    const start = startLocal.toISOString()
    const end = endLocal.toISOString()
    
    // Choose step dynamically based on range duration to keep data size reasonable
    const durationMs = endLocal.getTime() - startLocal.getTime()
    let step = '5m'
    if (durationMs > 30 * 24 * 60 * 60 * 1000) {
      step = '4h' // Greater than 30 days
    } else if (durationMs > 7 * 24 * 60 * 60 * 1000) {
      step = '1h' // Greater than 7 days
    } else if (durationMs > 2 * 24 * 60 * 60 * 1000) {
      step = '30m' // Greater than 2 days
    } else if (durationMs > 12 * 60 * 60 * 1000) {
      step = '10m' // Greater than 12h
    } else {
      step = '2m' // Less than 12h
    }
    
    // Form the Prometheus query
    const query = `esphome_sensor_value{id="temperature_${identifiant}", job="esphome-sensors", unit="°C"}`
    
    const response = await fetch(
      `${apiUrl}/prometheus/query-range?query=${encodeURIComponent(query)}&start=${start}&end=${end}&step=${step}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
    
    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`)
    }
    
    const data = await response.json()
    
    if (data.status !== 'success') {
      throw new Error(data.error || "Prometheus query failed.")
    }
    
    const result = data.data?.result
    if (!result || result.length === 0 || !result[0].values || result[0].values.length === 0) {
      throw new Error("No historical temperature data available for this range.")
    }
    
    const points: HistoryPoint[] = result[0].values.map((v: any) => {
      const timestampSecs = parseFloat(v[0])
      const tempVal = parseFloat(v[1])
      return {
        time: new Date(timestampSecs * 1000),
        value: tempVal
      }
    })
    
    historyData.value = points
  } catch (err: any) {
    console.error("Failed to fetch temperature history:", err)
    historyError.value = err.message || "Failed to load temperature history."
  } finally {
    historyLoading.value = false
  }
}

const openTemperatureHistory = (label: ObjectLabel) => {
  activeTempHistorySensor.value = label
  
  // Default range: yesterday and today (from yesterday 00:00 to today 23:59:59)
  const now = new Date()
  const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000)
  startDate.value = formatDateOnly(oneDayAgo)
  endDate.value = formatDateOnly(now)
  validationError.value = null
  
  fetchTempHistory()
}

const closeTempHistory = () => {
  activeTempHistorySensor.value = null
  historyData.value = []
  hoveredPointIndex.value = null
  startDate.value = ''
  endDate.value = ''
  validationError.value = null
}

// Compute metrics (min, max, average) for the loaded data
const historyStats = computed(() => {
  if (historyData.value.length === 0) {
    return { min: 0, max: 0, avg: 0 }
  }
  
  let min = Infinity
  let max = -Infinity
  let sum = 0
  
  for (const pt of historyData.value) {
    if (pt.value < min) min = pt.value
    if (pt.value > max) max = pt.value
    sum += pt.value
  }
  
  return {
    min,
    max,
    avg: sum / historyData.value.length
  }
})

// Map data points to SVG viewbox coordinates
const mappedPoints = computed(() => {
  const points = historyData.value
  if (points.length === 0) return []
  
  const firstPoint = points[0]
  const lastPoint = points[points.length - 1]
  if (!firstPoint || !lastPoint) return []
  
  const minTime = firstPoint.time.getTime()
  const maxTime = lastPoint.time.getTime()
  
  const vals = points.map(p => p.value)
  let minVal = Math.min(...vals)
  let maxVal = Math.max(...vals)
  
  // Pad the limits slightly for visual spacing
  const valRange = maxVal - minVal
  const pad = valRange > 0 ? valRange * 0.15 : 1
  minVal -= pad
  maxVal += pad
  
  const w = graphWidth - paddingLeft - paddingRight
  const h = graphHeight - paddingTop - paddingBottom
  
  return points.map((p, idx) => {
    const timeRatio = maxTime !== minTime ? (p.time.getTime() - minTime) / (maxTime - minTime) : 0.5
    const valRatio = maxVal !== minVal ? (p.value - minVal) / (maxVal - minVal) : 0.5
    
    const x = paddingLeft + timeRatio * w
    const y = graphHeight - paddingBottom - valRatio * h
    
    return {
      x,
      y,
      original: p,
      index: idx
    }
  })
})

// Generate SVG path definitions for the line and filled area
const graphPaths = computed(() => {
  const pts = mappedPoints.value
  const firstPt = pts[0]
  if (pts.length === 0 || !firstPt) {
    return { line: '', area: '' }
  }
  
  let linePath = `M ${firstPt.x} ${firstPt.y}`
  for (let i = 1; i < pts.length; i++) {
    const pt = pts[i]
    if (pt) {
      linePath += ` L ${pt.x} ${pt.y}`
    }
  }
  
  const lastPt = pts[pts.length - 1]
  if (!lastPt) return { line: '', area: '' }
  
  const bottomY = graphHeight - paddingBottom
  const areaPath = `${linePath} L ${lastPt.x} ${bottomY} L ${firstPt.x} ${bottomY} Z`
  
  return {
    line: linePath,
    area: areaPath
  }
})

// Generate positions for 4 Y-axis horizontal helper lines
const gridYLines = computed(() => {
  if (historyData.value.length === 0) return []
  
  const vals = historyData.value.map(p => p.value)
  let minVal = Math.min(...vals)
  let maxVal = Math.max(...vals)
  
  const valRange = maxVal - minVal
  const pad = valRange > 0 ? valRange * 0.15 : 1
  minVal -= pad
  maxVal += pad
  
  const lineCount = 4
  const lines = []
  const h = graphHeight - paddingTop - paddingBottom
  
  for (let i = 0; i < lineCount; i++) {
    const val = minVal + (i / (lineCount - 1)) * (maxVal - minVal)
    const valRatio = (val - minVal) / (maxVal - minVal)
    const y = graphHeight - paddingBottom - valRatio * h
    
    lines.push({
      val,
      y
    })
  }
  
  return lines
})

// Generate positions and labels for 5 X-axis vertical labels
const gridXLabels = computed(() => {
  const pts = mappedPoints.value
  if (pts.length === 0) return []
  
  const firstPt = pts[0]
  const lastPt = pts[pts.length - 1]
  if (!firstPt || !lastPt) return []
  
  const tickCount = 5
  const ticks = []
  
  // Check if range duration is greater than 24 hours
  const durationMs = lastPt.original.time.getTime() - firstPt.original.time.getTime()
  const isMultiDay = durationMs > 24 * 60 * 60 * 1000
  
  for (let i = 0; i < tickCount; i++) {
    const idx = Math.floor((i / (tickCount - 1)) * (pts.length - 1))
    const pt = pts[idx]
    if (pt) {
      const time = pt.original.time
      const hours = time.getHours().toString().padStart(2, '0')
      const minutes = time.getMinutes().toString().padStart(2, '0')
      
      // If multi-day, prepend "day/month" (DD/MM) to label
      const label = isMultiDay 
        ? `${time.getDate().toString().padStart(2, '0')}/${(time.getMonth() + 1).toString().padStart(2, '0')} ${hours}:${minutes}`
        : `${hours}:${minutes}`
      
      ticks.push({
        x: pt.x,
        label,
        time: time.getTime()
      })
    }
  }
  
  return ticks
})

// Handle cursor tracking on graph hover
const handleGraphMouseMove = (event: MouseEvent) => {
  const pts = mappedPoints.value
  if (pts.length === 0) return
  
  const svg = event.currentTarget as SVGElement
  const rect = svg.getBoundingClientRect()
  const mouseX = ((event.clientX - rect.left) / rect.width) * graphWidth
  
  let closestIndex = 0
  let minDiff = Infinity
  
  for (let i = 0; i < pts.length; i++) {
    const pt = pts[i]
    if (pt) {
      const diff = Math.abs(pt.x - mouseX)
      if (diff < minDiff) {
        minDiff = diff
        closestIndex = i
      }
    }
  }
  
  hoveredPointIndex.value = closestIndex
}

const handleGraphMouseLeave = () => {
  hoveredPointIndex.value = null
}

// Compute currently hovered data point and tooltip formatting
const hoveredPoint = computed(() => {
  if (hoveredPointIndex.value === null) return null
  const pt = mappedPoints.value[hoveredPointIndex.value]
  if (!pt) return null
  
  const time = pt.original.time
  const formattedTime = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' ' + time.toLocaleDateString([], { day: '2-digit', month: '2-digit' })
  
  let tooltipX = pt.x - 60
  if (tooltipX < 10) tooltipX = 10
  if (tooltipX > graphWidth - 130) tooltipX = graphWidth - 130
  
  return {
    x: pt.x,
    y: pt.y,
    value: pt.original.value,
    formattedTime,
    tooltipX
  }
})

let tempFetchInterval: ReturnType<typeof setInterval> | null = null

const fetchTemperatures = async () => {
  try {
    const config = useRuntimeConfig()
    const apiUrl = config.public.mintcoreApiUrl || 'http://localhost:3070'
    const token = config.public.mintcoreToken
    
    if (!token) {
      console.warn("NUXT_PUBLIC_MINTCORE_TOKEN is not defined in runtimeConfig")
      return
    }

    const options = {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
    
    const response = await fetch(`${apiUrl}/prometheus/query?query=esphome_sensor_value%7Bid%3D~%22temperature_.*%22%2C%20job%3D%22esphome-sensors%22%2C%20unit%3D%22%C2%B0C%22%7D`, options)
    const data = await response.json()
    
    if (data.status === 'success' && data.data?.result) {
      const results = data.data.result
      const tempMap: Record<string, number> = {}
      for (const res of results) {
        const id = res.metric?.id
        const valStr = res.value?.[1]
        if (id && valStr !== undefined) {
          tempMap[id] = parseFloat(valStr)
        }
      }
      
      objectLabels.value = objectLabels.value.map(label => {
        if (label.properties.identifiant) {
          const sensorId = `temperature_${label.properties.identifiant}`
          if (tempMap[sensorId] !== undefined) {
            return {
              ...label,
              properties: {
                ...label.properties,
                temperature: tempMap[sensorId]
              }
            }
          }
        }
        return label
      })
      console.log("Successfully fetched and updated temperatures:", tempMap)
      updateLabels()
    }
  } catch (err) {
    console.error("Failed to fetch temperatures:", err)
  }
}

const getLabelStyle = (label: ObjectLabel) => {
  const styles: Record<string, string> = {
    left: `${label.x}px`,
    top: `${label.y}px`,
  }

  let temp = label.properties.temperature
  if (temp !== undefined) {
    // Custom color palette (10 colors) from 18 ?C to 25 ?C
    const colors = [
      { r: 14, g: 190, b: 255 },  // #0ebeff
      { r: 41, g: 186, b: 234 },  // #29baea
      { r: 68, g: 181, b: 213 },  // #44b5d5
      { r: 94, g: 177, b: 192 },  // #5eb1c0
      { r: 121, g: 173, b: 171 }, // #79adab
      { r: 148, g: 168, b: 150 }, // #94a896
      { r: 175, g: 164, b: 129 }, // #afa481
      { r: 201, g: 160, b: 108 }, // #c9a06c
      { r: 228, g: 155, b: 87 },  // #e49b57
      { r: 255, g: 151, b: 66 }   // #ff9742
    ]
    
    const clamped = Math.max(18, Math.min(25, temp))
    const ratio = (clamped - 18) / (25 - 18) // 0 to 1

    const position = ratio * (colors.length - 1)
    const index = Math.floor(position)
    const fraction = position - index

    let r, g, b
    if (index >= colors.length - 1) {
      const c = colors[colors.length - 1]!
      r = c.r
      g = c.g
      b = c.b
    } else {
      const c1 = colors[index]!
      const c2 = colors[index + 1]!
      r = Math.round(c1.r + (c2.r - c1.r) * fraction)
      g = Math.round(c1.g + (c2.g - c1.g) * fraction)
      b = Math.round(c1.b + (c2.b - c1.b) * fraction)
    }
    
    // Background with transparency and matching border
    styles.background = `rgba(${r}, ${g}, ${b}, 0.25)`
    styles.borderColor = `rgba(${r}, ${g}, ${b}, 0.65)`
    styles.boxShadow = `0 4px 15px rgba(${r}, ${g}, ${b}, 0.25)`
  }

  return styles
}

const findLevelForNode = (node: any): string | undefined => {
  if (!viewer) return undefined
  
  // Try directly on the node first
  const directLevel = node.model?.raw?.level || node.model?.level?.name || node.model?.level || node.model?.parameters?.Niveau
  if (directLevel) {
    const val = extractPropertyValue(directLevel)
    if (val) return String(val)
  }

  // Otherwise check ancestors
  const ancestors = viewer.getWorldTree().getAncestors(node)
  for (let i = ancestors.length - 1; i >= 0; i--) {
    const ancestor = ancestors[i]
    if (ancestor) {
      const levelVal = ancestor.model?.raw?.level || ancestor.model?.level?.name || ancestor.model?.level || ancestor.model?.parameters?.Niveau
      if (levelVal) {
        const val = extractPropertyValue(levelVal)
        if (val) return String(val)
      }
    }
  }
  return undefined
}

onMounted(async () => {
  if (!speckleContainer.value) return
  
  // Attach pointerdown listener in CAPTURE phase to get priority over Speckle
  speckleContainer.value.addEventListener('pointerdown', handleViewerClick, { capture: true })


  // Configure viewer parameters
  const params = { ...DefaultViewerParams }
  params.showStats = false // Hide default stats in favor of our custom UI
  params.verbose = false

  // Initialize the viewer
  viewer = new Viewer(speckleContainer.value, params)
  await viewer.init()

  // Set up ResizeObserver to handle container resize and update label positions
  resizeObserver = new ResizeObserver(() => {
    if (viewer) {
      viewer.resize()
    }
    updateLabels()
  })
  resizeObserver.observe(speckleContainer.value)

  // Add necessary extensions for interaction
  const cameraController = viewer.createExtension(CameraController)
  if (cameraController) {
    cameraController.on(CameraEvent.FrameUpdate, () => {
      updateLabels()
    })
  }
  const selectionExt = viewer.createExtension(SelectionExtension)
  viewer.createExtension(FilteringExtension)
  
  // Create and add COPC Extension
  const copcExt = viewer.createExtension(CopcExtension)
  const copcPromise = copcExt.setConfiguration({
    url: 'https://pc1.realbuild.app/v2/gl-rc/rc-ext.copc-pdal.laz',
    pointSize: 0.07 // 10cm points
  })

  // Listen to selection events directly on the viewer instance
  viewer.on(ViewerEvent.ObjectClicked, (selectionInfo: any) => {
    console.log("=== Selection Event Triggered (New API) ===", selectionInfo)
    
    // Si selectionInfo existe et contient des hits
    if (selectionInfo && selectionInfo.hits && selectionInfo.hits.length > 0) {
      const hit = selectionInfo.hits[0]
      console.log("First Hit Data:", hit)
      
      // La structure peut être hit.object, hit.node.model, ou autre
      // On log le hit et on essaye d'extraire les données
      if (hit.node && hit.node.model) {
        console.log("Found properties in hit.node.model")
        selectedObject.value = hit.node.model
      } else if (hit.object) {
        console.log("Found properties in hit.object")
        selectedObject.value = hit.object
      } else {
        console.log("Fell back to storing whole hit")
        selectedObject.value = hit
      }
    } else {
      console.log("Clic dans le vide (désélection)")
      selectedObject.value = null
    }
  })

  // Listen to load complete event to extract labels when geometry is fully ready
  viewer.on(ViewerEvent.LoadComplete, async () => {
    console.log("=== Viewer Load Complete Event ===")
    if (!viewer) return
    
    const levelsSet = new Set<string>()
    let hasObjectsWithoutLevel = false
    let debugLoggedCount = 0
    
    viewer.getWorldTree().findAll((node: any) => {
      if (debugLoggedCount < 5) {
        console.log("DEBUG NODE KEYS:", {
          nodeKeys: Object.keys(node || {}),
          modelKeys: Object.keys(node.model || {}),
          rawKeys: Object.keys(node.model?.raw || {}),
          rawBbox: node.model?.raw?.bbox || node.model?.raw?.boundingBox,
          modelBbox: node.model?.bbox || node.model?.boundingBox,
          nodeBbox: node?.bbox || node?.boundingBox,
          rawId: node.model?.raw?.id
        })
        debugLoggedCount++
      }
      const levelName = node.model?.raw?.level || node.model.level?.name || node.model.level || node.model.parameters?.Niveau || ''
      
      if (!levelName || levelName === 'null' || levelName === '') {
        hasObjectsWithoutLevel = true
      } else {
        levelsSet.add(levelName.toString())
      }
      return true // just iterating
    })
    
    const levelsArray = Array.from(levelsSet).sort()
    if (hasObjectsWithoutLevel) {
      levelsArray.push('Aucun')
    }
    
    availableLevels.value = levelsArray

    // Extract labels for named objects, placing them at the top of their bounding boxes
    const labels: ObjectLabel[] = []
    viewer.getWorldTree().walk((node: any) => {
      const name = node.model?.raw?.name
      if (name && node.model?.id) {
        let center = new THREE.Vector3()
        let hasGeometry = false
        if (node.model.renderView?.aabb) {
          const aabb = node.model.renderView.aabb
          center.set(
            (aabb.min.x + aabb.max.x) / 2,
            (aabb.min.y + aabb.max.y) / 2,
            aabb.max.z
          )
          hasGeometry = true
        } else {
          // Compute the bounding box of composite object children
          const box = new THREE.Box3()
          node.walk((child: any) => {
            if (child.model?.renderView?.aabb) {
              box.union(child.model.renderView.aabb)
              hasGeometry = true
            }
            return true
          })
          if (hasGeometry) {
            center.set(
              (box.min.x + box.max.x) / 2,
              (box.min.y + box.max.y) / 2,
              box.max.z + 0.2
            )
          }
        }
        if (hasGeometry) {
          const rawProps = node.model.raw?.properties
          const worksetNameVal = rawProps?.worksetName
          const levelVal = findLevelForNode(node)
          const identifiantVal = rawProps?.Parameters?.['Instance Parameters']?.["Données d'identification"]?.Identifiant

          labels.push({
            id: node.model.id,
            name: String(name),
            category: node.model.raw?.category ? String(node.model.raw.category) : undefined,
            position3D: center,
            x: 0,
            y: 0,
            visible: false,
            properties: {
              name: String(name),
              level: levelVal !== undefined ? String(extractPropertyValue(levelVal)) : undefined,
              worksetName: worksetNameVal !== undefined ? String(extractPropertyValue(worksetNameVal)) : undefined,
              identifiant: identifiantVal !== undefined ? String(extractPropertyValue(identifiantVal)) : undefined
            }
          })
        }
      }
      return true
    })
    objectLabels.value = labels
    console.log(`Successfully loaded ${labels.length} named object labels`)
    labels.slice(0, 10).forEach(l => {
      console.log(`Label Debug: ${l.name} (cat: ${l.category}) position3D =`, JSON.stringify(l.position3D))
    })
    updateLabels()
    
    // Fetch temperatures immediately
    await fetchTemperatures()
    
    // Start polling every 10 seconds
    if (tempFetchInterval) {
      clearInterval(tempFetchInterval)
    }
    tempFetchInterval = setInterval(fetchTemperatures, 10000)
  })

  // Load a demonstration Speckle stream
  try {
    const urls = await UrlHelper.getResourceUrls(
      // "https://app.speckle.systems/projects/65dea96898/models/4fdc2b59fe"
      "https://app.speckle.systems/projects/bad2345566/models/e66ce2ad0f"
    )
    
    for (const url of urls) {
      if (viewer) {
        const speckleToken = config.public.speckleToken || undefined
        const loader = new SpeckleLoader(viewer.getWorldTree(), url, speckleToken)
        await viewer.loadObject(loader, true)
      }
    }
  } catch (error) {
    console.error("Failed to load Speckle model:", error)
  }
    
  // Wait for the COPC point cloud to load entirely
  try {
    await copcPromise
  } catch (e) {
    console.warn("Error loading COPC point cloud:", e)
  }
  
  // Calculate bounding boxes and move the point cloud
  try {
    if (viewer) {
      const renderer = (viewer as any).getRenderer()
      if (renderer && renderer.scene) {

        // // Temporarily hide the point cloud group to compute the Speckle model BBox alone
        // copcExt.pointCloudGroup.visible = false
        
        // // Force update of matrices
        // renderer.scene.updateMatrixWorld(true)
        
        // // Calculer la BBox du modèle Speckle de manière plus ciblée
        // let speckleBox = new THREE.Box3()
        // if (renderer.sceneBox && typeof renderer.sceneBox.isEmpty === 'function' && !renderer.sceneBox.isEmpty()) {
        //     speckleBox.copy(renderer.sceneBox)
        //     debugInfo.value += `Using renderer.sceneBox\n`
        // } else {
        //     // Parcourir la scène et éviter les helpers (lumières, grilles, caméras, etc.)
        //     renderer.scene.children.forEach((child: any) => {
        //         if (
        //             child !== copcExt.pointCloudGroup && 
        //             !child.isLight && 
        //             !child.isCamera && 
        //             !child.isGridHelper && 
        //             !child.isAxesHelper &&
        //             !(child.name || '').toLowerCase().includes('grid')
        //         ) {
        //             const childBox = new THREE.Box3().setFromObject(child)
        //             if (!childBox.isEmpty() && isFinite(childBox.min.x)) {
        //                 speckleBox.union(childBox)
        //             }
        //         }
        //     })
        //     debugInfo.value += `Using setFromObject filter\n`
        // }
        
        // debugInfo.value += `Speckle BBox: min(${speckleBox.min.x.toFixed(1)}, ${speckleBox.min.y.toFixed(1)}, ${speckleBox.min.z.toFixed(1)}) max(${speckleBox.max.x.toFixed(1)}, ${speckleBox.max.y.toFixed(1)}, ${speckleBox.max.z.toFixed(1)})\n`
        
        // // Restore the point cloud visibility
        // copcExt.pointCloudGroup.visible = showPointCloud.value
        
        // Display Speckle BBox in green
        // if (!speckleBox.isEmpty() && isFinite(speckleBox.min.x)) {
        //     const speckleBoxHelper = new THREE.Box3Helper(speckleBox, new THREE.Color(0x00ff00))
        //     ;(speckleBoxHelper.material as THREE.Material).depthTest = false // Toujours visible au-dessus du modèle
        //     renderer.scene.add(speckleBoxHelper)
        // }
        
        // Scale the COPC point cloud geometry (100x)
        const scaleFactor = 100
        copcExt.pointCloudGroup.scale.set(scaleFactor, scaleFactor, scaleFactor)
        
        // Calculer la BBox du nuage de points COPC
        copcExt.pointCloudGroup.updateMatrixWorld(true)
        const copcBox = new THREE.Box3().setFromObject(copcExt.pointCloudGroup)
        
        // debugInfo.value += `COPC Initial BBox: min(${copcBox.min.x.toFixed(1)}, ${copcBox.min.y.toFixed(1)}, ${copcBox.min.z.toFixed(1)}) max(${copcBox.max.x.toFixed(1)}, ${copcBox.max.y.toFixed(1)}, ${copcBox.max.z.toFixed(1)})\n`
        
        if (!copcBox.isEmpty() && isFinite(copcBox.min.x)) {
            // Appliquer les valeurs par défaut
            applyTransform()
            
            // VITAL: Re-add the group to the active scene since Speckle Loader recreates it!
            if (!renderer.scene.children.includes(copcExt.pointCloudGroup)) {
                renderer.scene.add(copcExt.pointCloudGroup)
                // debugInfo.value += `Re-added pointCloudGroup to active scene\n`
            }
            
            // Update the COPC BBox with the translation so it is positioned correctly
            copcBox.setFromObject(copcExt.pointCloudGroup)
            
            // Display COPC BBox in red
            const copcBoxHelper = new THREE.Box3Helper(copcBox, new THREE.Color(0xff0000))
            ;(copcBoxHelper.material as THREE.Material).depthTest = false // Toujours visible
            renderer.scene.add(copcBoxHelper)
        } else {
            debugInfo.value += `Error: Could not compute bounds (Empty Box)\n`
            if (copcBox.isEmpty()) {
                debugInfo.value += `COPC point group has no geometry bounds. Make sure computeBoundingBox() was called.\n`
            }
        }
        
        // Request a new render of the scene
        if (typeof viewer.requestRender === 'function') {
          viewer.requestRender()
        }
      }
    }
  } catch (error) {
    console.error("Error with post-load processing:", error)
  } finally {
    loading.value = false
    
    // Hide the overlay after 15 seconds
    hideOverlayTimeout = setTimeout(() => {
      showOverlay.value = false
    }, 5000)
  }
})

onBeforeUnmount(() => {
  if (speckleContainer.value) {
    speckleContainer.value.removeEventListener('pointerdown', handleViewerClick, { capture: true })
  }
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
  if (hideOverlayTimeout) {
    clearTimeout(hideOverlayTimeout)
  }
  if (tempFetchInterval) {
    clearInterval(tempFetchInterval)
  }
  if (viewer) {
    viewer.dispose()
  }
})
</script>

<style scoped>
.viewer-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 500px;
  overflow: hidden;
  background: #111; /* Fallback for dark aesthetic */
}

.speckle-container {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.sidebar-overlay {
  position: absolute;
  left: 16px;
  top: 16px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  z-index: 10;
}

.sidebar-tools {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sidebar-panels {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.filter-toggle-btn {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(22, 22, 29, 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  color: var(--text-primary, #f0f0f5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  transition: all 0.2s ease;
  position: relative;
  flex-shrink: 0;
}

.filter-toggle-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: scale(1.05);
}

/* Filter Button NEW */
.filter-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--text-primary, #f0f0f5);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  gap: 8px;
}

.filter-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--accent-color, #00c8cb);
}

.filter-button.is-active {
  background: rgba(0, 200, 203, 0.2);
  color: var(--accent-color, #00c8cb);
  border: 1px solid rgba(0, 200, 203, 0.5);
}

.filter-button:active {
  transform: scale(0.95);
}

.active-filter-indicator {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--accent-color, #00c8cb);
  box-shadow: 0 0 8px var(--accent-color, #00c8cb);
}

.filter-dropdown {
  background: rgba(22, 22, 29, 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  padding: 16px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  max-height: 80vh;
  overflow-y: auto;
}

.filter-dropdown::-webkit-scrollbar {
  width: 4px;
}
.filter-dropdown::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
}

/* Transform Panel */
.transform-panel {
  width: 220px;
}
.input-group {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
}
.input-group label {
  color: var(--text-secondary, #8f8f9d);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  width: 14px;
  font-weight: 600;
  text-align: right;
}
.input-group input {
  flex: 1;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: white;
  padding: 6px 8px;
  font-family: monospace;
  font-size: 13px;
  width: 100%;
  transition: all 0.2s;
}
.input-group input:hover {
  background: rgba(0, 0, 0, 0.6);
  border-color: rgba(255, 255, 255, 0.2);
}
.input-group input:focus {
  outline: none;
  border-color: var(--accent-color, #00c8cb);
  background: rgba(0, 0, 0, 0.6);
  box-shadow: 0 0 0 2px rgba(0, 200, 203, 0.2);
}
.block-mt {
  margin-top: 16px;
}
.block-mt:first-child {
  margin-top: 4px;
}
.block-mb {
  margin-bottom: 24px;
}

.picker-help {
  font-size: 0.85rem;
  color: #ccc;
  margin-bottom: 12px;
  line-height: 1.4;
}

.picked-results {
  background: rgba(0, 0, 0, 0.4);
  padding: 12px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.coord-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.coord-label {
  color: #888;
  font-weight: bold;
}

.coord-val {
  font-family: monospace;
  color: var(--accent-color, #00c8cb);
}

.copy-btn {
  width: 100%;
  padding: 8px;
  background: var(--accent-color, #00c8cb);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: opacity 0.2s;
}

.copy-btn:hover {
  opacity: 0.9;
}

.viewer-container {
  /* Dynamic border to show state */
  border: 2px solid v-bind("isPicking ? '#00c8cb' : 'transparent'");
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.3s;
  /* Cursor change when picking */
  cursor: v-bind("isPicking ? 'crosshair' : 'default'");
}

.indicator-picking {
  background-color: #00c8cb !important;
  box-shadow: 0 0 8px #00c8cb;
}

/* Transition for fade-slide */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  min-height: 40px;
  color: var(--text-secondary, #8f8f9d);
  font-family: 'Inter', sans-serif;
  font-size: 13px;
}

.filter-title {
  margin: 0 0 8px 0;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary, #8f8f9d);
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

/* Info Sidebar */
.info-sidebar {
  position: absolute;
  right: 16px;
  top: 16px;
  bottom: 16px;
  width: 320px;
  background: rgba(22, 22, 29, 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  z-index: 10;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: var(--text-primary, #f0f0f5);
  font-family: 'Inter', sans-serif;
}

.info-sidebar h3 {
  margin: 0;
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.2);
}

.close-btn {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.properties-list {
  padding: 16px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.properties-list::-webkit-scrollbar {
  width: 6px;
}

.properties-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
}

.properties-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.property-item {
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.2);
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.03);
}

.property-key {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary, #8f8f9d);
  margin-bottom: 4px;
  font-weight: 600;
}

.property-value {
  font-size: 14px;
  word-break: break-word;
  color: #e2e8f0;
}

.overlay {
  position: absolute;
  top: 16px;
  right: 16px;
  pointer-events: none; /* Let clicks pass through to the 3D viewer */
  color: white;
  z-index: 5;
}

h2 {
  margin: 0 0 8px 0;
  font-family: 'Inter', sans-serif;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.5px;
  text-shadow: 0 2px 10px rgba(0,0,0,0.5);
}

p {
  margin: 0 0 24px 0;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  opacity: 0.8;
  text-shadow: 0 1px 5px rgba(0,0,0,0.5);
}

.stats {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 8px 16px;
  border-radius: 20px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.pulse, .pulse-small {
  background-color: #00c8cb;
  border-radius: 50%;
  animation: pulse-animation 2s infinite;
}
.pulse {
  width: 8px;
  height: 8px;
}
.pulse-small {
  width: 6px;
  height: 6px;
}

.success {
  width: 8px;
  height: 8px;
  background-color: #10b981;
  border-radius: 50%;
  box-shadow: 0 0 10px #10b981;
}

.debug-overlay {
  position: absolute;
  bottom: 16px;
  left: 16px;
  background: rgba(0, 0, 0, 0.7);
  color: #0f0;
  padding: 12px;
  border-radius: 8px;
  font-family: monospace;
  font-size: 12px;
  z-index: 100;
  pointer-events: none;
  max-width: 400px;
  white-space: pre-wrap;
}

@keyframes pulse-animation {
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(0, 200, 203, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(0, 200, 203, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(0, 200, 203, 0); }
}

/* Transition for fade out */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Labels Overlay styles */
.labels-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 8;
  overflow: hidden;
}

.object-label {
  position: absolute;
  transform: translate(-50%, -50%);
  background: rgba(22, 22, 29, 0.75);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 0 1em 1em 1em;
  color: #f0f0f5;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.15s ease, background-color 0.15s ease;
}

.object-label:hover {
  filter: brightness(1.2);
  transform: translate(-50%, -50%) scale(1.05);
  z-index: 9;
}

.label-text {
  user-select: none;
}

/* Temperature Modal Backdrop and Glassmorphic Content */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 15, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 999;
  display: flex;
  overflow-y: auto;
  padding: 20px 10px;
  box-sizing: border-box;
}

.modal-content {
  margin: auto;
  width: 650px;
  max-width: 90vw;

  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6), 0 0 40px rgba(0, 200, 203, 0.1);
  overflow: hidden;
  animation: modal-glow 6s infinite alternate;
}

@keyframes modal-glow {
  from { box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6), 0 0 40px rgba(0, 200, 203, 0.05); }
  to { box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6), 0 0 40px rgba(0, 200, 203, 0.15); }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-icon {
  font-size: 24px;
}

.modal-title h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.modal-title .subtitle {
  margin: 2px 0 0 0;
  font-size: 12px;
  color: var(--text-secondary);
  opacity: 1;
  text-shadow: none;
}

.modal-close-btn {
  background: rgba(255, 255, 255, 0.05);
  border: none;
  color: var(--text-secondary);
  font-size: 14px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  color: var(--text-primary);
  transform: scale(1.05);
}

.modal-body {
  padding: 16px;
}

.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 280px;
  gap: 16px;
  color: var(--text-secondary);
}

.pulse-large {
  width: 12px;
  height: 12px;
  background-color: var(--accent-color);
  border-radius: 50%;
  animation: pulse-animation 2s infinite;
}

.modal-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 280px;
  gap: 16px;
  color: #ef4444;
}

.retry-btn {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: #ef4444;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-btn:hover {
  background: rgba(239, 68, 68, 0.2);
}

.graph-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.stat-card {
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  text-align: center;
}

.stat-label {
  font-size: 11px;
  text-transform: uppercase;
  color: var(--text-secondary);
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 16px;
  font-weight: 700;
}

.stat-value.current { color: var(--accent-color); }
.stat-value.min { color: #3b82f6; }
.stat-value.max { color: #ef4444; }
.stat-value.avg { color: #10b981; }

.svg-graph-wrapper {
  position: relative;
  background: rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  padding: 10px 0;
  overflow: visible;
}

.svg-graph-container {
  position: relative;
  width: 100%;
  height: 260px;
}

.svg-graph {
  display: block;
  overflow: visible;
}

.graph-tooltip {
  position: absolute;
  background: rgba(22, 22, 29, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 8px 12px;
  pointer-events: none;
  z-index: 10;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
  width: 110px;
  text-align: center;
  transition: left 0.1s ease, top 0.1s ease;
}

.tooltip-time {
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 2px;
}

.tooltip-val {
  font-size: 13px;
  font-weight: 700;
  color: var(--accent-color);
}

/* Tooltip indicator dot */
.graph-dot {
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: var(--accent-color);
  border: 2px solid #fff;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 9;
  box-shadow: 0 0 8px rgba(0, 200, 203, 0.5);
  transition: left 0.1s ease, top 0.1s ease;
}

/* Transition for Fade & Scale */
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: opacity 0.3s ease;
}

.fade-scale-enter-active .modal-content,
.fade-scale-leave-active .modal-content {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease;
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
}

.fade-scale-enter-from .modal-content,
.fade-scale-leave-to .modal-content {
  transform: scale(0.95);
  opacity: 0;
}

.modal-controls {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  margin-bottom: 24px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
}

.date-input-group {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 6px;
}

.date-input-group label {
  font-size: 11px;
  text-transform: uppercase;
  color: var(--text-secondary);
  font-weight: 600;
  letter-spacing: 0.5px;
}

.datetime-input {
  height: 38px;
  box-sizing: border-box;
  background: rgba(22, 22, 29, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 0 12px;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  outline: none;
  transition: all 0.2s ease;
  width: 100%;
  max-width:270px;
  color-scheme: dark; /* Force dark mode styling for the browser native datepicker popup */
}

.datetime-input:focus {
  border-color: var(--accent-color);
  box-shadow: 0 0 0 2px rgba(0, 200, 203, 0.2);
  background: rgba(22, 22, 29, 0.9);
}

.datetime-input::-webkit-calendar-picker-indicator {
  cursor: pointer;
  opacity: 1;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.datetime-input::-webkit-calendar-picker-indicator:hover {
  transform: scale(1.1);
  opacity: 0.85;
}

.refresh-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 200, 203, 0.1);
  border: 1px solid rgba(0, 200, 203, 0.25);
  color: var(--accent-color);
  width: 100%;
  height: 38px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-btn:hover {
  background: var(--accent-color);
  color: #111;
  transform: translateY(-1px);
}

.refresh-btn:active {
  transform: translateY(1px);
}

.validation-error-msg {
  color: #ef4444;
  font-size: 12px;
  margin-top: -12px;
  margin-bottom: 16px;
  padding-left: 4px;
}

/* Responsive styles for tablet and desktop */
@media (min-width: 601px) {
  .modal-header {
    padding: 20px 24px;
  }

  .modal-body {
    padding: 24px;
  }

  .stats-row {
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }

  .modal-controls {
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-start;
    gap: 30px;
  }

  .date-input-group {
    flex: 1;
    max-width: 220px;
  }

  .refresh-btn {
    width: 38px;
    margin-left: auto;
  }
}
</style>
